/**
 * Which screens of the navigation registry are BUILT.
 *
 * The router decides what a path renders (`IMPLEMENTED` in routes.tsx); the sidebar and the command palette need
 * to know the same thing without importing the router, which imports the shell, which imports them. So the list
 * lives here, on its own, and an app test holds it equal to the router's — a screen cannot be built and still say
 * "soon", or say nothing and show a specification.
 */
import { HUBS } from '@rtcom/domain';

export const IMPLEMENTED_SCREEN_IDS: ReadonlySet<string> = new Set([
  'admin.integrations',
  'overview.control-center',
  'overview.attention',
  'operations.minor-loci',
  'operations.major-loci',
  'admin.approvals',
  'operations.minor-locus-360',
  'operations.major-locus-360',
  'admin.users',
  'admin.user-360',
  'admin.invitations',
  'admin.roles',
  'admin.permissions',
  'admin.application-management',
  'admin.api-management',
  'admin.access',
  'admin.master-data',
  'admin.settings', 'admin.control-centre',
  'admin.roadmap',
  'ops.packing-orders',
  'b2b.accounts', 'b2b.dispatch', 'b2b.challan', 'b2b.bill', 'plant.ghee-butter',
  'delivery.customers-today',
  'operations.collection-sessions',
  'operations.collection',
  'operations.batches',
  'operations.quality',
  'operations.dispatch',
  'operations.transit',
  'operations.plant-inward',
  'plant.chillers',
  'plant.filling-lots',
  'plant.production',
  'operations.barrels',
  'plant.inventory',
  'analytics.trace',
  'plant.production-lots',
  'workforce.assets',
  'plant.quality',
  // EPIC 29 · the plant's people and phones
  'plant.people',
  'plant.devices',
  'plant.messages',
  'admin.notifications',
  'operations.milk-tests',
  'operations.chillers',
  'operations.farms',
  'plant.application',
  'delivery.application',
  'delivery.maps',
  // Delivery D11 · the console
  'delivery.map',
  'delivery.rounds',
  'delivery.approvals',
  'delivery.staff',
  'delivery.riders',
  'logistics.routes',
  'delivery.bottles',
  'delivery.dashboard',
  'delivery.orders',
  'overview.milk-chain',
  'workforce.person-360',
  'customers.customer-360-launcher',
  'workforce.incentives',
  'delivery.plan',
  'delivery.patterns',
  'delivery.attendance',
  'delivery.devices',
  'delivery.trace',
  'delivery.reports',
  'delivery.sync',
  'plant.figures',
  'plant.approvals',
  'finance.transactions',
  'finance.ledger-check',
  'finance.invoices',
  'finance.notes',
  'finance.late-charge',
  'finance.tax-rates',
  'finance.templates',
  'plant.dashboard',
  'plant.operations',
  'plant.filling',
  // EPIC 30 · the Farm console
  'farm.dashboard',
  'farm.major-loci',
  'farm.major-locus',
  'farm.minor-loci',
  'farm.minor-locus',
  'farm.onboarding',
  'farm.payments',
  'farm.people',
  'farm.application',
  'farm.sync',
  'farm.trace',
  'farm.attention',
  'farm.reports',
  'farm.statement',
  'workforce.people',
  'workforce.attendance',
  'workforce.shifts',
  'admin.audit-logs',
  'customers.master',
  'customers.customer-360',
  'customers.subscriptions',
  'customers.orders',
  'customers.subscription-360',
  'customers.order-detail-view',
  'customers.dated-changes',
  // EPIC 13 · the round
  'logistics.delivery-calendar',
  'logistics.delivery-planning',
  'logistics.trips',
  'logistics.stops',
  'logistics.delivery',
  'logistics.returns',
  'logistics.delivery-exceptions',
  'logistics.pod',
  'customers.deliveries',
  'customers.bottles',
  'operations.data-health',
  'logistics.day-preview',
  'logistics.riders',
  'logistics.delivery-performance',
  'logistics.round-claims',
  'logistics.field-reports',
  'logistics.delivery-corrections',
  'logistics.delivery-moves',
  'customers.status-changes',
  'customers.custody-corrections',
  // v7 CORE-1
  'supply.notices', 'crm.onboarding', 'crm.start', 'crm.performance', 'crm.money', 'crm.bottles', 'crm.payments', 'crm.messages', 'money.ghee-bills', 'money.banks', 'money.milk-bills', 'crm.overview', 'automation.overview', 'automation.whatsapp', 'automation.reminders', 'automation.broadcast', 'automation.settings', 'automation.instructions', 'automation.learnings', 'automation.outcomes', 'crm.delivery-changes', 'ops.day-close', 'ops.rider-reports', 'ops.courier-confirm', 'ops.overview', 'supply.milk-flow', 'supply.farm-summary', 'supply.plant-summary', 'supply.trace-lookup', 'supply.day-map', 'supply.quality-report', 'customers.complaints', 'crm.requests', 'crm.leads', 'crm.inquiries', 'crm.inquiry-insights', 'crm.sales', 'crm.ghee-overview', 'crm.ghee-today', 'crm.ghee-upcoming', 'crm.ghee-customers', 'crm.ghee-variants', 'crm.ghee-billing', 'crm.ghee-courier', 'crm.ghee-recurring', 'crm.ghee-reminders', 'money.prices', 'admin.import-centre', 'insights.map', 'admin.qr',
  'home.my-day', 'home.erp-changes', 'insights.director', 'insights.reports', 'crm.reports', 'ops.reports', 'ops.rider-order', 'ops.rider-rules', 'ops.variant-colours', 'supply.reports', 'money.reports',
  'people.reports', 'admin.uat-reset',
]);

/** Built: implemented, or a hub (v7) whose tabs are built screens. */
export const isBuilt = (screenId: string): boolean => IMPLEMENTED_SCREEN_IDS.has(screenId) || screenId in HUBS;
